/**
 * Users — Layout route for the Users module.
 *
 * Promoted from /admin/users to a top-level rail item because user management
 * in a regulated-industry AI platform is a first-class compliance surface,
 * not a subpage of Admin. Mirrors the PaaS pattern.
 *
 * Seven sub-tabs:
 *   - Users        — list, filters, bulk actions, create wizard → detail page
 *   - Groups       — named user bundles (security / user / dynamic)
 *   - Roles        — permission templates + role-permission matrix
 *   - Invitations  — pending invites queue
 *   - Departments  — org structure
 *   - Activity     — identity events stream (logins, MFA, password changes)
 *   - SSO          — the tenant's own OIDC / SAML 2.0 connections: add, test,
 *                    edit, remove (Foundations 2 / B2d-T4)
 *
 * Detail routes (hide tabs): /users/$userId
 */

import { createFileRoute, Outlet, useLocation } from '@tanstack/react-router';
import { ContentHeader } from '@/components/content-header';
import {
  Users as UsersIcon, UsersRound, ShieldCheck, Send, Building2, Activity,
  KeyRound,
} from 'lucide-react';

export const Route = createFileRoute('/users')({
  component: UsersLayout,
});

const USERS_TABS = [
  { label: 'Users',        path: '/users',              icon: UsersIcon },
  { label: 'Groups',       path: '/users/groups',       icon: UsersRound },
  { label: 'Roles',        path: '/users/roles',        icon: ShieldCheck },
  { label: 'Invitations',  path: '/users/invitations',  icon: Send },
  { label: 'Departments',  path: '/users/departments',  icon: Building2 },
  { label: 'Activity',     path: '/users/activity',     icon: Activity },
  // SSO is a self-service tenant surface (Foundations 2 / B2d-T4): the tab
  // renders the connection list at `routes/users/sso.tsx` against
  // `/api/v1/users/sso`, whose one store is `tenant_sso_providers`. The note
  // that used to sit here described a clarity placeholder and named two API
  // route files that task DELETED — a comment naming a deleted file is how the
  // next reader writes code against a store that is no longer there.
  { label: 'SSO',          path: '/users/sso',          icon: KeyRound },
];

function UsersLayout() {
  const location = useLocation();
  // Hide tabs on detail routes like /users/user-sarah-chen
  const showTabs = USERS_TABS.some(t =>
    t.path === location.pathname || location.pathname === t.path + '/',
  );

  return (
    <div className="flex flex-col h-full">
      {showTabs && <ContentHeader title="Users" tabs={USERS_TABS} />}
      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
}
